import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, CopyPlus, Send, Wrench, X } from "lucide-react";
import { api } from "@/lib/api";
import { WhatsAppButton } from "@/components/WhatsApp";
import { useAuth } from "@/lib/auth";
import { act, useFetch } from "@/lib/hooks";
import { can, fmtDate } from "@/lib/format";
import { Field, Loading, PageHeader, Panel, PdfButton, StatusBadge } from "@/components/common";
import { LinesEditor, LinesView, Totals } from "@/components/LinesEditor";
import { selectCls } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function DecisionDialog({ quote, decision, onClose, onDone }: any) {
  const [by, setBy] = useState(quote?.client_name || "");
  const [method, setMethod] = useState("Signature sur place");
  const [note, setNote] = useState("");
  const save = async () => {
    if (await act(() => api.post(`/quotes/${quote.id}/decision`, { decision, by_name: by, method, note }), decision === "accepte" ? "Accord du client enregistré" : "Refus enregistré")) onDone();
  };
  return (
    <Dialog open={!!decision} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="decision-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{decision === "accepte" ? "Accord du client" : "Refus du client"}</DialogTitle></DialogHeader>
        <Field label="Nom de la personne"><Input value={by} onChange={(e) => setBy(e.target.value)} data-testid="decision-by-input" /></Field>
        <Field label="Moyen">
          <select className={selectCls} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="decision-method-select">
            {["Signature sur place", "Téléphone", "Email", "SMS / WhatsApp"].map((m) => <option key={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Remarque"><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="decision-note-input" /></Field>
        <p className="text-xs text-muted-foreground">La décision, l'heure, le montant et votre nom sont tracés dans le journal d'audit.</p>
        <Button className="h-11" onClick={save} data-testid="decision-save-btn">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function QuoteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: q, reload } = useFetch<any>(`/quotes/${id}`);
  const settings = useFetch<any>("/settings");
  const [lines, setLines] = useState<any[]>([]);
  const [diag, setDiag] = useState("");
  const [decision, setDecision] = useState<string | null>(null);
  const [mechs, setMechs] = useState<any[]>([]);
  const [mech, setMech] = useState("");

  useEffect(() => {
    if (q) {
      setLines(q.lines);
      setDiag(q.diagnosis || "");
    }
  }, [q]);
  useEffect(() => {
    if (can(user?.role, "orders_manage")) api.get("/users/mechanics").then((r) => setMechs(r.data));
  }, [user]);
  if (!q) return <Loading />;

  const save = () => act(() => api.put(`/quotes/${q.id}`, { reception_id: q.reception_id, diagnosis: diag, lines, notes: q.notes }), "Devis enregistré").then((r) => r && reload());
  const send = async () => {
    const saved = await act(() => api.put(`/quotes/${q.id}`, { reception_id: q.reception_id, diagnosis: diag, lines, notes: q.notes }));
    if (saved && (await act(() => api.post(`/quotes/${q.id}/send`), "Devis présenté et figé"))) reload();
  };
  const revise = async () => {
    const r = await act(() => api.post(`/quotes/${q.id}/revise`), "Nouvelle version créée");
    if (r) nav(`/devis/${r.data.id}`);
  };
  const openOrder = async () => {
    const r = await act(() => api.post("/orders", { quote_id: q.id, mechanic_id: mech || null }), "Ordre de réparation ouvert");
    if (r) nav(`/or/${r.data.id}`);
  };
  const isLatest = q.versions[q.versions.length - 1]?.id === q.id;

  return (
    <div>
      <PageHeader eyebrow={`Devis · version ${q.version}`} title={q.number}
        actions={<>
          <PdfButton kind="quote" id={q.id} label="PDF devis" />
          {["envoye", "accepte", "refuse"].includes(q.status) && <WhatsAppButton kind="quote" id={q.id} label="Envoyer par WhatsApp" onSent={reload} testId="quote-whatsapp-btn" />}
          {q.status === "brouillon" && <><Button variant="outline" onClick={save} data-testid="quote-save-btn">Enregistrer</Button><Button onClick={send} data-testid="quote-send-btn"><Send className="mr-2 h-4 w-4" />Présenter au client</Button></>}
          {q.status === "envoye" && <><Button variant="outline" onClick={() => setDecision("refuse")} data-testid="quote-refuse-btn"><X className="mr-2 h-4 w-4" />Refus</Button><Button onClick={() => setDecision("accepte")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="quote-accept-btn"><Check className="mr-2 h-4 w-4" />Accord client</Button></>}
          {["envoye", "refuse", "accepte"].includes(q.status) && isLatest && !q.order && <Button variant="outline" onClick={revise} data-testid="quote-revise-btn"><CopyPlus className="mr-2 h-4 w-4" />Nouvelle version</Button>}
        </>}>
        <div className="mt-3 flex items-center gap-3"><StatusBadge status={q.status} testId="quote-status" /><span className="text-sm text-muted-foreground"><span className="font-mono-data">{q.plate}</span> · {q.vehicle_label} · {q.client_name} · valable jusqu'au {q.valid_until}</span></div>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <Panel title="Diagnostic">
            {q.status === "brouillon" ? <Textarea rows={3} value={diag} onChange={(e) => setDiag(e.target.value)} placeholder="Constat du chef d'atelier…" data-testid="quote-diagnosis-input" /> : <p className="text-sm">{q.diagnosis || "—"}</p>}
          </Panel>
          <Panel title="Lignes du devis">
            {q.status === "brouillon" ? <LinesEditor lines={lines} onChange={setLines} defaultTva={settings.data?.tva_rate ?? 19} /> : <><LinesView lines={q.lines} /><Totals totals={q.totals} /></>}
          </Panel>
        </div>
        <div className="space-y-6">
          {q.decision && (
            <Panel title="Décision client" testId="quote-decision-panel">
              <div className="space-y-1 text-sm">
                <StatusBadge status={q.decision.decision} />
                <div className="pt-2">{q.decision.by_name} · {q.decision.method}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(q.decision.at)} · saisi par {q.decision.recorded_by}</div>
                {q.decision.note && <div className="text-xs">{q.decision.note}</div>}
              </div>
            </Panel>
          )}
          {q.status === "accepte" && (
            <Panel title="Ordre de réparation">
              {q.order ? (
                <Link to={`/or/${q.order.id}`} className="flex items-center justify-between rounded-sm border border-border p-3 hover:bg-accent" data-testid="quote-order-link"><span className="font-mono-data">{q.order.number}</span><StatusBadge status={q.order.status} /></Link>
              ) : can(user?.role, "orders_manage") ? (
                <div className="space-y-3">
                  <select className={selectCls} value={mech} onChange={(e) => setMech(e.target.value)} data-testid="order-mechanic-select">
                    <option value="">Mécanicien (à affecter plus tard)</option>
                    {mechs.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <Button className="h-12 w-full" onClick={openOrder} data-testid="open-order-btn"><Wrench className="mr-2 h-4 w-4" />Ouvrir l'OR</Button>
                </div>
              ) : <p className="text-sm text-muted-foreground">Le chef d'atelier ouvrira l'OR.</p>}
            </Panel>
          )}
          <Panel title="Versions">
            {q.versions.map((v: any) => (
              <Link key={v.id} to={`/devis/${v.id}`} className={`flex items-center justify-between border-b border-border py-2 text-sm last:border-0 ${v.id === q.id ? "font-semibold" : ""}`} data-testid={`quote-version-${v.version}`}>
                <span>v{v.version} · {fmtDate(v.created_at)}</span><StatusBadge status={v.status} />
              </Link>
            ))}
          </Panel>
          <Panel title="Historique">
            {[...q.history, ...(q.whatsapp || []).map((e: any) => ({ at: e.at, by: e.by, event: `envoyé par WhatsApp au +${e.to}` }))]
              .sort((a: any, b: any) => a.at.localeCompare(b.at))
              .map((h: any, i: number) => (
                <div key={i} className="border-l-2 border-border py-1.5 pl-3 text-xs" data-testid={h.event.startsWith("envoyé par WhatsApp") ? "quote-whatsapp-history" : undefined}><div>{h.event}</div><div className="text-muted-foreground">{fmtDate(h.at)} · {h.by}</div></div>
              ))}
          </Panel>
        </div>
      </div>
      <DecisionDialog quote={q} decision={decision} onClose={() => setDecision(null)} onDone={() => { setDecision(null); reload(); }} />
    </div>
  );
}
